// @ts-nocheck
import { generateClient } from "aws-amplify/api";
import { datasetsByOrganizationIdAndSlug } from "../graphql/queries";
import { v4 as uuidv4 } from "uuid";
import { formatTimeAgo } from "./dateUtils";
import { downloadTextFile } from "./fileUtils";
import { observedColumns } from "./datasetColumnMapping";

const client = generateClient();

/** Generate a URL-safe slug from a dataset name @param name - The dataset name to convert @returns A lowercase, hyphenated slug */
export const generateSlug = (name: string): string => {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')  // Replace non-alphanumeric with hyphens
    .replace(/^-+|-+$/g, '')       // Remove leading/trailing hyphens
    .replace(/--+/g, '-');         // Replace multiple hyphens with single
};

/** Check if a slug is unique within an organization @param organizationId - The organization ID @param slug - The slug to check @returns True if unique, false otherwise */
export const isSlugUnique = async (
  organizationId: string,
  slug: string
): Promise<boolean> => {
  // Personal datasets don't need unique slugs across the system
  if (!organizationId || organizationId === "") {
    return true;
  }

  try {
    console.log("Checking slug uniqueness for:", { organizationId, slug });
    
    const { data } = await client.graphql({
      query: datasetsByOrganizationIdAndSlug,
      variables: {
        organizationId,
        slug: { eq: slug }
      }
    });

    const items = data?.datasetsByOrganizationIdAndSlug?.items || [];
    console.log("Found existing datasets with same slug:", items.length);
    return items.length === 0;
  } catch (error) {
    console.error("Error checking slug uniqueness:", error);
    // Return true to allow creation even if check fails
    return true;
  }
};

/** Generate a unique slug by appending numbers if needed @param organizationId - The organization ID @param baseName - The base name to generate slug from @param existingSlugs - Optional array of existing slugs to check against (for offline validation) @returns A unique slug */
export const generateUniqueSlug = async (
  organizationId: string,
  baseName: string,
  existingSlugs?: string[]
): Promise<string> => {
  const baseSlug = generateSlug(baseName);
  let slug = baseSlug;
  let counter = 1;

  // If we have a local list of slugs, check against that first (faster)
  if (existingSlugs) {
    while (existingSlugs.includes(slug)) {
      slug = `${baseSlug}-${counter}`;
      counter++;
    }
  }

  // For personal datasets (no organizationId), just return the slug
  if (!organizationId) {
    return slug;
  }

  // Then verify with the database
  try {
    while (!(await isSlugUnique(organizationId, slug))) {
      slug = `${baseSlug}-${counter}`;
      counter++;
      // Prevent infinite loop
      if (counter > 100) {
        console.warn("Too many slug iterations, using timestamp");
        slug = `${baseSlug}-${Date.now()}`;
        break;
      }
    }
  } catch (error) {
    console.error("Error generating unique slug, using timestamp fallback:", error);
    slug = `${baseSlug}-${Date.now()}`;
  }

  return slug;
};

/** Generate the S3 path for a dataset @param organizationId - Organization ID (optional) @param datasetId - The dataset UUID @param cognitoIdentityId - User's cognito identity ID (for personal datasets) @returns The S3 path string */
export const generateS3Path = (
  organizationId: string | null,
  datasetId: string,
  cognitoIdentityId: string
): string => {
  if (organizationId) {
    return `organizationprivate/${organizationId}/datasets/${datasetId}/`;
  } else {
    return `private/${cognitoIdentityId}/datasets/${datasetId}/`;
  }
};

/** Format bytes to human-readable size @param bytes - Number of bytes @returns Formatted string (e.g., "1.2 MB") */
export const formatFileSize = (bytes: number): string => {
  if (!bytes) return "0 B";
  const sizes = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(2)} ${sizes[i]}`;
};

/** Validate CSV content structure @param csvContent - The CSV content as string @param expectedColumns - Expected column headers @returns Object with validation result and error message */
export const validateCSV = (
  csvContent: string,
  expectedColumns?: string[]
): { valid: boolean; error?: string; rowCount?: number } => {
  try {
    const lines = csvContent.trim().split('\n');
    
    if (lines.length === 0) {
      return { valid: false, error: "CSV file is empty" };
    }

    const headers = lines[0].toLowerCase().split(',').map(h => h.trim());
    
    // Check if it has at least timestamp and value columns
    if (!headers.includes('timestamp') || !headers.includes('value')) {
      return { 
        valid: false, 
        error: "CSV must have 'timestamp' and 'value' columns" 
      };
    }

    // If expected columns provided, check they exist
    if (expectedColumns) {
      const missingColumns = expectedColumns.filter(
        col => !headers.includes(col.toLowerCase())
      );
      if (missingColumns.length > 0) {
        return { 
          valid: false, 
          error: `Missing columns: ${missingColumns.join(', ')}` 
        };
      }
    }

    // Validate at least one data row exists
    if (lines.length < 2) {
      return { 
        valid: false, 
        error: "CSV must contain at least one data row" 
      };
    }

    return { 
      valid: true, 
      rowCount: lines.length - 1 // Subtract header row
    };
  } catch (error) {
    return { 
      valid: false, 
      error: `Invalid CSV format: ${error.message}` 
    };
  }
};

/** Parse location string/object into consistent format @param location - Location input (string or object) @returns Parsed location object */
export const parseLocation = (location: any): any => {
  if (!location) return null;

  // If it's already an object, return as is
  if (typeof location === 'object') {
    return location;
  }

  // If it's a string, try to parse as JSON
  if (typeof location === 'string') {
    try {
      return JSON.parse(location);
    } catch {
      // If not JSON, return as name
      return { name: location };
    }
  }

  return { name: String(location) };
};

/** Generate a meaningful dataset ID with prefix. The id addresses the dataset in the REST API and is part of
 * its S3 path, so it leads with the dataset's own name (slugified, trimmed) to stay recognisable in an upload
 * script or an S3 listing; the UUID keeps it unique.
 * @param datasetType - HISTORICAL (the only type a dataset can be)
 * @param name - Dataset name
 * @returns e.g. "hist-skagit-discharge-2f1c…" */
export const generateDatasetId = (
  datasetType: string,
  name?: string
): string => {
  // TODO(dead-code #994): the fcst branch is unreachable, every caller passes HISTORICAL
  const typePrefix = datasetType === 'HISTORICAL' ? 'hist' : 'fcst';
  // Whole words only, so the id never ends mid-word: "skagit-river-disc" reads worse than "skagit-river"
  const nameSlug = generateSlug(name || '')
    .split('-')
    .reduce((slug, word) => (!slug ? word : slug.length + 1 + word.length <= 24 ? `${slug}-${word}` : slug), '');
  const prefix = nameSlug || 'data';
  const uuid = uuidv4();
  return `${typePrefix}-${prefix}-${uuid}`;
};

// ============================================ Date Range Utilities ============================================

export const ONE_YEAR_MS = 365 * 24 * 60 * 60 * 1000;

/** Convert a date to ISO string or null */
export const toISOStringOrNull = (date: Date | string | null | undefined): string | null => {
  return date ? new Date(date).toISOString() : null;
};

/** Clamp a date range to a maximum of one year, respecting optional min/max bounds */
export const clampRangeToOneYear = (
  start: Date | string | null | undefined,
  end: Date | string | null | undefined,
  options: { minDate?: Date | string | null; maxDate?: Date | string | null } = {}
): { start: Date | null; end: Date | null } => {
  const min = options.minDate ? new Date(options.minDate) : null;
  const max = options.maxDate ? new Date(options.maxDate) : null;
  let endDate = end ? new Date(end) : max ? new Date(max) : null;
  let startDate = start ? new Date(start) : null;

  if (!startDate && endDate) {
    startDate = new Date(endDate.getTime() - ONE_YEAR_MS);
  }
  if (startDate && !endDate) {
    endDate = new Date(Math.min(startDate.getTime() + ONE_YEAR_MS, max ? max.getTime() : startDate.getTime() + ONE_YEAR_MS));
  }
  if (!startDate && !endDate) {
    return { start: null, end: null };
  }

  if (startDate && endDate) {
    const diff = endDate.getTime() - startDate.getTime();
    if (diff > ONE_YEAR_MS) {
      startDate = new Date(endDate.getTime() - ONE_YEAR_MS);
    }
  }

  if (max && endDate && endDate > max) {
    endDate = new Date(max);
    if (startDate && endDate.getTime() - startDate.getTime() > ONE_YEAR_MS) {
      startDate = new Date(endDate.getTime() - ONE_YEAR_MS);
    }
  }

  if (min && startDate && startDate < min) {
    startDate = new Date(min);
  }
  if (min && endDate && endDate < min) {
    endDate = new Date(min);
  }

  return { start: startDate, end: endDate };
};

/** Parse a dataset's timeRange JSON field into start/end dates */
export const parseDatasetTimeRange = (
  timeRange: string | { startDate: string; endDate: string } | null | undefined
): { start: Date; end: Date } | null => {
  if (!timeRange) return null;
  try {
    const parsed = typeof timeRange === "string" ? JSON.parse(timeRange) : timeRange;
    if (parsed?.startDate && parsed?.endDate) {
      return {
        start: new Date(parsed.startDate),
        end: new Date(parsed.endDate),
      };
    }
  } catch (error) {
    console.warn("Failed to parse dataset timeRange", error);
  }
  return null;
};

interface Dataset {
  timeRange?: string | null;
  lastUpdated?: string | null;
}

interface DateRangeState {
  start?: Date | string | null;
  end?: Date | string | null;
}

/** Calculate the effective date range for historical dataset visualization */
export const getHistoricalRangeInfo = (
  dataset: Dataset,
  fallbackDays: number,
  storedRange?: DateRangeState | null
): {
  startDate: Date | null;
  endDate: Date | null;
  minDate: Date | null;
  maxDate: Date | null;
} => {
  const parsedRange = parseDatasetTimeRange(dataset.timeRange);
  const minDate = parsedRange?.start ? new Date(parsedRange.start) : null;
  const maxDate = parsedRange?.end ? new Date(parsedRange.end) : null;
  let startDate = storedRange?.start ? new Date(storedRange.start) : null;
  let endDate = storedRange?.end ? new Date(storedRange.end) : null;

  if (!endDate) {
    if (maxDate) {
      endDate = new Date(maxDate);
    } else if (dataset.lastUpdated) {
      endDate = new Date(dataset.lastUpdated);
    } else {
      endDate = new Date();
    }
  }

  if (!startDate) {
    const startCandidate = new Date(endDate);
    startCandidate.setDate(startCandidate.getDate() - fallbackDays);
    if (minDate && startCandidate < minDate) {
      startDate = new Date(minDate);
    } else {
      startDate = startCandidate;
    }
  }

  const clampedRange = clampRangeToOneYear(startDate, endDate, { minDate, maxDate });

  return {
    startDate: clampedRange.start,
    endDate: clampedRange.end,
    minDate,
    maxDate,
  };
};

/** Format date to short ISO format (without seconds) Returns: YYYY-MM-DDTHH:MMZ */
export const formatDateShortISO = (dateString: string | Date | null | undefined): string => {
  if (!dateString) return "-";
  const date = new Date(dateString);
  const isoString = date.toISOString();
  // Remove seconds and milliseconds (everything after the minutes)
  return isoString.substring(0, 16) + 'Z';
};

// ============================================ CSV Download Utilities ============================================

/** Escape a value for CSV output (handles quotes, commas, newlines) */
export const escapeCSVValue = (value: unknown): string => {
  if (value === null || value === undefined) return "";
  const stringValue = value instanceof Date ? value.toISOString() : String(value);
  if (/[",\n]/.test(stringValue)) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }
  return stringValue;
};

/** Convert data array to CSV string with proper column ordering */
export const convertDataToCSV = (
  data: Record<string, unknown>[],
  columnOrder?: string[]
): string => {
  if (!data || data.length === 0) return "";

  // Determine column order
  let columns = columnOrder;
  if (!columns || columns.length === 0) {
    const firstRow = data[0];
    const keys = Object.keys(firstRow || {}).filter((key) => key !== "id");
    if (keys.includes("timestamp")) {
      columns = ["timestamp", ...keys.filter((key) => key !== "timestamp")];
    } else {
      columns = keys;
    }
  }

  const sanitizedColumns = columns.filter((col) => col && col !== "id");

  const csvRows = data.map((row) =>
    sanitizedColumns
      .map((column) => escapeCSVValue(row[column]))
      .join(",")
  );

  return [sanitizedColumns.join(","), ...csvRows].join("\n");
};

/** Trigger a CSV download in the browser */
export const downloadCSV = (csvContent: string, filename: string): void => {
  downloadTextFile(csvContent, filename, "text/csv");
};

// ============================================ Customer-facing Display Helpers ============================================

// Enum -> the words a customer reads. "ENSEMBLE_FORECAST" is schema vocabulary, not English. The two forecast types
// are retired (phase 3 of docs/plans/DATA_PIPELINES_CONVERGENCE_PLAN.md): no new row can be made and uploads are
// refused; a leftover row is shown as what it is.
const DATASET_TYPE_LABELS: Record<string, string> = {
  HISTORICAL: "Historical",
  FORECAST: "Forecast (retired)",
  ENSEMBLE_FORECAST: "Ensemble (retired)"
};

export const formatDatasetType = (datasetType?: string | null): string =>
  DATASET_TYPE_LABELS[datasetType || ""] || "Dataset";

// A leftover FORECAST / ENSEMBLE_FORECAST row: the types are retired, so it is read-only (no chart, no uploads).
export const isForecastDataset = (dataset: any): boolean =>
  dataset?.datasetType === "FORECAST" || dataset?.datasetType === "ENSEMBLE_FORECAST";

// What the dataset holds, as a customer reads it. Units and parameters live on each column, so the dataset states how many columns rather than one parameter
export const describeDatasetColumns = (dataset: any): string => {
  const count = observedColumns(dataset).length;
  if (count === 0) return "No columns yet";
  return `${count} ${count === 1 ? "column" : "columns"}`;
};

// How much data is behind the dataset — forecast datasets count files, historical ones rows, which is why the table's bare "Count" column meant two things per row
export const describeDatasetVolume = (dataset: any): string => {
  const count = dataset?.recordCount || 0;
  if (isForecastDataset(dataset)) {
    return `${count.toLocaleString()} forecast ${count === 1 ? "file" : "files"}`;
  }
  return `${count.toLocaleString()} ${count === 1 ? "record" : "records"}`;
};

// The one line a customer scans for: is this dataset current? Tone carries the answer as well as the words, so a stale or empty dataset reads at a glance
export const describeDatasetFreshness = (
  dataset: any
): { label: string; tone: string } => {
  if (isForecastDataset(dataset)) {
    return { label: "Retired forecast dataset: use an external forecast model", tone: "text-eai-ink-faint" };
  }
  if (!dataset?.recordCount) return { label: "No data uploaded yet", tone: "text-eai-ink-faint" };
  const updated = formatTimeAgo(dataset?.lastUpdated);
  if (!updated) return { label: "Never updated", tone: "text-eai-ink-faint" };
  return { label: `Updated ${updated}`, tone: "text-eai-ink-subtle" };
};

// The fetch arguments for a dataset's chart, kept out of the component: the day window and the clamped range are one decision
export const buildDatasetDataRequest = (
  dataset: any,
  options: { range?: any } = {}
): { params: any; rangeInfo: any; effectiveRange: any } => {
  const days = 30;
  const rangeInfo = getHistoricalRangeInfo(dataset, days, options.range);
  const effectiveRange = clampRangeToOneYear(
    options.range?.start || rangeInfo?.startDate,
    options.range?.end || rangeInfo?.endDate,
    { minDate: rangeInfo?.minDate, maxDate: rangeInfo?.maxDate }
  );

  return {
    params: {
      datasetId: dataset?.id,
      days: null,
      startDate: toISOStringOrNull(effectiveRange?.start),
      endDate: toISOStringOrNull(effectiveRange?.end)
    },
    rangeInfo,
    effectiveRange
  };
};

// Download what the chart is showing, in the dataset's stored column order; lifted off the page so the detail modal owns one call rather than the parsing dance
export const downloadDatasetCSV = (dataset: any, data: any[], shownColumns: string[] = []): void => {
  if (!data || data.length === 0) return;

  // shownColumns: the caller knows exactly which columns it is showing (a curated dataset's query answer)
  let columnOrder: string[] = shownColumns;
  if (columnOrder.length === 0 && dataset?.columns) {
    try {
      const parsed = JSON.parse(dataset.columns);
      if (Array.isArray(parsed) && parsed.length > 0) columnOrder = parsed;
    } catch (err) {
      console.warn("Failed to parse stored columns for dataset:", dataset?.id, err);
    }
  }

  const fileSlug = dataset?.slug || dataset?.id;
  const filename = `${fileSlug}_displayed_${new Date().toISOString().split("T")[0]}.csv`;
  downloadCSV(convertDataToCSV(data, columnOrder), filename);
};

export type DatasetDetailTab = "overview" | "uploads" | "processing";

export const DATASET_DETAIL_TABS: { id: DatasetDetailTab; label: string }[] = [
  { id: "overview", label: "Overview" },
  { id: "uploads", label: "Uploads" },
  { id: "processing", label: "Processing" },
];

/** A dataset with no data has no chart to show, so its detail view opens on the upload log: where a failed first upload is explained. */
export const initialDatasetDetailTab = (dataset: { recordCount?: number | null } | null | undefined): DatasetDetailTab =>
  dataset?.recordCount ? "overview" : "uploads";

// Sort mirrors Model Garden's toolbar options so the list pages stay consistent
export const DATASET_SORT_OPTIONS = [
  { value: "name", label: "Name A–Z" },
  { value: "recent", label: "Recently updated" },
  { value: "size", label: "Largest" },
];

export const sortDatasets = (datasets: any[], sortBy: string): any[] => {
  const time = (value?: string | null) => (value ? new Date(value).getTime() : 0);
  const sorted = [...datasets];
  switch (sortBy) {
    case "recent":
      return sorted.sort((a, b) => time(b.lastUpdated) - time(a.lastUpdated));
    case "size":
      return sorted.sort((a, b) => (b.fileSize || 0) - (a.fileSize || 0));
    default:
      return sorted.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }
};

/**
 * Whether the user may see models on the Data & Models page: every signed-in user, once their groups are known
 * (stage 4 of docs/plans/HISTORICAL_PROCESSED_MODELS_PLAN.md, decision 1). AppSync already limits the list to the
 * models they own or share through their organization; before this stage the screens hid them from non-admins.
 */
export const groupsResolved = (groups: string[] | null | undefined): boolean => Array.isArray(groups);

/**
 * Mirrors Dataset @auth for editing: Admin, or the dataset's owner. Org members only have [read], so an org
 * dataset is editable only by whoever created it. `owner` is stored in more than one format (see ownerScope.ts),
 * and `userId` is the creator's Cognito username, so both are matched against every candidate.
 */
export const canManageDataset = (
  dataset: { owner?: string | null; userId?: string | null } | null | undefined,
  groups: string[] | null | undefined,
  ownerCandidates: string[]
): boolean => {
  if (!dataset) return false;
  if (groups?.includes("Admin")) return true;
  return ownerCandidates.some((candidate) => candidate && (dataset.owner === candidate || dataset.userId === candidate));
};

/**
 * Mirrors the upload API's rule (canUploadToDataset in erodeapifunction/src/datasetUploads.js): Admin; for an
 * organization dataset, any member of that organization (its Cognito group); otherwise the dataset's owner.
 */
export const canUploadToDataset = (
  dataset: { owner?: string | null; userId?: string | null; organizationId?: string | null } | null | undefined,
  groups: string[] | null | undefined,
  ownerCandidates: string[]
): boolean => {
  if (!dataset) return false;
  if (groups?.includes("Admin")) return true;
  if (dataset.organizationId) return Boolean(groups?.includes(dataset.organizationId));
  return canManageDataset(dataset, groups, ownerCandidates);
};
